<template>
  <div class="wo-resources" :class="{ 'wo-resources--compact': compact }">
    <div v-if="!hideHeader" class="wo-resources__header">
      <h3 class="wo-resources__title">
        <BaseIcon name="cubes" size="14" />
        {{ $t('resources.requirement.title') }}
        <span v-if="conflictCount" class="wo-resources__conflict-count">
          <BaseIcon name="exclamation-triangle" size="11" />
          {{ conflictCount }}
        </span>
      </h3>
      <div v-if="!readonly" class="wo-resources__header-actions">
        <button class="wo-resources__link" :disabled="deriving" @click="derive">
          {{ $t('resources.actions.derive') }}
        </button>
        <button class="wo-resources__link wo-resources__link--primary" @click="openAdd">
          + {{ $t('resources.actions.add_requirement') }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="wo-resources__loading"><LoadingSpinner size="sm" /></div>

    <p v-else-if="!requirements.length" class="wo-resources__empty">
      {{ $t('resources.requirement.empty') }}
    </p>

    <ul v-else class="wo-resources__list">
      <li
        v-for="req in requirements"
        :key="req.id"
        class="wo-resources__item"
        :class="{
          'wo-resources__item--conflict': req.conflicts?.length,
          'wo-resources__item--done': req.status === 'returned',
        }"
      >
        <div class="wo-resources__item-main">
          <div class="wo-resources__item-top">
            <span class="wo-resources__item-label">{{ requirementLabel(req) }}</span>
            <Label :variant="statusVariant(req.status)" size="sm">
              {{ $t(`resources.requirement_statuses.${req.status}`) }}
            </Label>
            <Label v-if="req.fulfillment === 'rent'" variant="primary" size="sm">
              {{ $t('resources.fulfillment.rent') }}
            </Label>
          </div>
          <div class="wo-resources__item-meta">
            <span v-if="req.from_date">{{ formatDateRange(req.from_date, req.to_date) }}</span>
            <span v-else>{{ $t('resources.requirement.no_date') }}</span>
            <span
              v-if="
                req.resource?.parent_resource_id && vehicleName(req.resource.parent_resource_id)
              "
            >
              ·
              <BaseIcon name="truck" size="11" />
              {{ vehicleName(req.resource.parent_resource_id) }}
            </span>
            <span v-if="req.source === 'product_rule'" class="wo-resources__source">
              · {{ $t('resources.requirement.source_product_rule') }}
            </span>
            <span v-if="req.rental_supplier_name">· {{ req.rental_supplier_name }}</span>
            <span v-if="req.rental_cost != null">· {{ formatCurrency(req.rental_cost) }}</span>
          </div>
          <div v-if="req.conflicts?.length" class="wo-resources__conflict">
            <BaseIcon name="exclamation-triangle" size="12" />
            {{
              $t('resources.requirement.conflict_detail', {
                date: formatDate(req.conflicts[0].date),
                required: req.conflicts[0].required,
                capacity: req.conflicts[0].capacity,
              })
            }}
          </div>
          <p v-if="req.note" class="wo-resources__note">{{ req.note }}</p>
        </div>

        <div v-if="!readonly" class="wo-resources__item-actions">
          <Btn
            v-if="canTransition(req, 'reserved')"
            variant="outlined"
            size="xs"
            :loading="busyId === req.id"
            @click="setStatus(req, 'reserved')"
          >
            {{ $t('resources.actions.mark_reserved') }}
          </Btn>
          <Btn
            v-if="
              canTransition(req, 'rented') ||
              (req.fulfillment === 'rent' && req.status !== 'returned')
            "
            variant="outlined"
            size="xs"
            icon="edit"
            @click="openRental(req)"
          >
            {{
              req.status === 'rented' || req.status === 'on_site'
                ? $t('resources.actions.rental_details')
                : $t('resources.actions.mark_rented')
            }}
          </Btn>
          <Btn
            v-if="canTransition(req, 'on_site')"
            variant="outlined"
            size="xs"
            :loading="busyId === req.id"
            @click="setStatus(req, 'on_site')"
          >
            {{ $t('resources.actions.mark_on_site') }}
          </Btn>
          <Btn
            v-if="canTransition(req, 'returned')"
            variant="secondary"
            size="xs"
            icon="check"
            :loading="busyId === req.id"
            @click="setStatus(req, 'returned')"
          >
            {{ $t('resources.actions.mark_returned') }}
          </Btn>
          <button
            class="wo-resources__icon-btn"
            @click="confirmRemove(req)"
            :title="$t('common.delete')"
          >
            <BaseIcon name="trash" size="13" />
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, watch, getCurrentInstance } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { resourceService } from '@/services/resourceService';
  import {
    REQUIREMENT_STATUS_VARIANTS,
    REQUIREMENT_TRANSITIONS,
    requirementLabel,
    formatDateRange,
  } from '@/constants/resourceConstants';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import AddResourceRequirementModal from '@/components/resources/AddResourceRequirementModal.vue';
  import RentalDetailsModal from '@/components/resources/RentalDetailsModal.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default defineComponent({
    name: 'WorkOrderResourcesCard',
    components: { Btn, Label, LoadingSpinner, BaseIcon },
    props: {
      workOrder: { type: Object, required: true },
      compact: { type: Boolean, default: false },
      readonly: { type: Boolean, default: false },
      hideHeader: { type: Boolean, default: false },
    },
    emits: ['changed'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const deriving = ref(false);
      const busyId = ref(null);
      const requirements = ref([]);
      const vehicles = ref([]);

      const conflictCount = computed(
        () => requirements.value.filter((r) => r.conflicts?.length).length
      );

      const statusVariant = (status) => REQUIREMENT_STATUS_VARIANTS[status] || 'default';
      const canTransition = (req, to) => (REQUIREMENT_TRANSITIONS[req.status] || []).includes(to);
      const vehicleName = (id) => vehicles.value.find((v) => v.id === id)?.name || '';
      const formatDate = (key) =>
        key
          ? new Date(`${key}T00:00:00`).toLocaleDateString('nl-NL', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            })
          : '';
      const formatCurrency = (value) =>
        new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
          Number(value) || 0
        );

      const load = async () => {
        if (!props.workOrder?.id) return;
        loading.value = true;
        try {
          const [reqs, vehs] = await Promise.allSettled([
            resourceService.getWorkOrderRequirements(props.workOrder.id),
            vehicles.value.length
              ? Promise.resolve(vehicles.value)
              : resourceService.getAll({ kind: 'vehicle' }),
          ]);
          requirements.value = reqs.status === 'fulfilled' ? reqs.value : [];
          vehicles.value = vehs.status === 'fulfilled' ? vehs.value : [];
        } finally {
          loading.value = false;
        }
      };

      watch(
        () => [props.workOrder?.id, props.workOrder?.appointment_start],
        () => load(),
        { immediate: true }
      );

      const notifyError = (error, fallbackKey) =>
        toastStore.addToast({
          type: 'error',
          message: error?.response?.data?.error || $t(fallbackKey),
        });

      const openAdd = () => {
        const modalId = uiStore.addModal(AddResourceRequirementModal, {
          title: $t('resources.actions.add_requirement'),
          size: 'lg',
          workOrder: props.workOrder,
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async (payload) => {
            try {
              await resourceService.addWorkOrderRequirement(props.workOrder.id, payload);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('resources.messages.requirement_added'),
              });
              await load();
              emit('changed');
            } catch (error) {
              notifyError(error, 'resources.messages.requirement_failed');
            }
          },
        });
      };

      const derive = async () => {
        deriving.value = true;
        try {
          const result = await resourceService.deriveWorkOrderRequirements(props.workOrder.id);
          toastStore.addToast({
            type: result.created ? 'success' : 'info',
            message: result.created
              ? $t('resources.messages.derived', { count: result.created })
              : $t('resources.messages.derived_none'),
          });
          await load();
          emit('changed');
        } catch (error) {
          notifyError(error, 'resources.messages.requirement_failed');
        } finally {
          deriving.value = false;
        }
      };

      const setStatus = async (req, status) => {
        busyId.value = req.id;
        try {
          await resourceService.updateWorkOrderRequirement(props.workOrder.id, req.id, { status });
          await load();
          emit('changed');
        } catch (error) {
          notifyError(error, 'resources.messages.requirement_failed');
        } finally {
          busyId.value = null;
        }
      };

      const openRental = (req) => {
        const modalId = uiStore.addModal(RentalDetailsModal, {
          title: $t('resources.rental.title'),
          size: 'lg',
          requirement: { ...req, work_order: props.workOrder },
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async (payload) => {
            try {
              await resourceService.updateWorkOrderRequirement(props.workOrder.id, req.id, payload);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('resources.messages.requirement_updated'),
              });
              await load();
              emit('changed');
            } catch (error) {
              notifyError(error, 'resources.messages.requirement_failed');
            }
          },
        });
      };

      const confirmRemove = (req) => {
        const modalId = uiStore.addModal(false, {
          title: $t('resources.requirement.remove_title'),
          description: $t('resources.requirement.remove_description', {
            label: requirementLabel(req),
          }),
          status: 'danger',
          submitText: $t('common.delete'),
          cancelText: $t('common.cancel'),
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async () => {
            try {
              await resourceService.deleteWorkOrderRequirement(props.workOrder.id, req.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('resources.messages.requirement_removed'),
              });
              await load();
              emit('changed');
            } catch (error) {
              notifyError(error, 'resources.messages.requirement_failed');
            }
          },
        });
      };

      return {
        loading,
        deriving,
        busyId,
        requirements,
        conflictCount,
        statusVariant,
        canTransition,
        vehicleName,
        formatDate,
        formatCurrency,
        formatDateRange,
        requirementLabel,
        openAdd,
        derive,
        setStatus,
        openRental,
        confirmRemove,
      };
    },
  });
</script>

<style scoped lang="scss">
  .wo-resources {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      margin: 0;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--slate-500);
    }

    &__conflict-count {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      font-size: 0.6875rem;
      color: var(--amber-700);
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      border-radius: 999px;
      padding: 0 6px;
      text-transform: none;
      letter-spacing: 0;
    }

    &__header-actions {
      display: flex;
      gap: 0.75rem;
    }

    &__link {
      background: none;
      border: none;
      padding: 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
      cursor: pointer;

      &--primary {
        color: var(--brand-600);
      }

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      @include hover {
        text-decoration: underline;
      }
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 0.75rem;
    }

    &__empty,
    &__note {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__item {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      background: white;

      &--conflict {
        border-color: var(--amber-300);
        background: var(--amber-50);
      }

      &--done {
        opacity: 0.6;
      }
    }

    &__item-main {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      min-width: 0;
    }

    &__item-top {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__item-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__item-meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.75rem;
      color: var(--slate-500);

      span {
        display: inline-flex;
        align-items: center;
        gap: 0.2rem;
      }
    }

    &__conflict {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--amber-800);
    }

    &__item-actions {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      flex-wrap: wrap;
    }

    &__icon-btn {
      margin-left: auto;
      background: none;
      border: none;
      padding: 0.25rem;
      color: var(--slate-400);
      cursor: pointer;
      border-radius: 4px;

      @include hover {
        color: var(--red-600);
        background: var(--red-50);
      }
    }

    &--compact {
      .wo-resources__item {
        padding: 0.5rem 0.625rem;
      }
    }
  }
</style>
